import { decodeBase64, encodeBase64, getIdentifier, WithId } from "@medplum/core";
import { DocumentReference, DocumentReferenceContent } from "@medplum/fhirtypes";

import DOMPurify from "dompurify";

import { Systems } from "@/fhir/enums/systems";

import {
  DOT_PHRASE_ARCHIVED_STATUS,
  DOT_PHRASE_BODY_CONTENT_TYPE,
  DOT_PHRASE_PREVIEW_MAX_LENGTH,
  DOT_PHRASE_SEPARATOR_MARKER,
  DOT_PHRASE_TITLE_BODY_SEPARATOR,
  DOT_PHRASE_TRIGGER_PREFIX,
} from "../constants/dot-phrases";
import { DOT_PHRASE_ALLOWED_BODY_TAGS } from "../constants/phrase-text-format";
import { DotPhrase } from "../types/dot-phrase";
import type { DotPhraseFormType } from "../zod-schema/dot-phrase";

/** `</p>`, `</li>` and `<br>` are where a provider pressed Enter or Shift+Enter. Without turning
 * them into newlines, `<p>a</p><p>b</p>` becomes `ab` and a search matches across a paragraph
 * break. */
const BLOCK_BOUNDARY_PATTERN = /<br\s*\/?>|<\/(?:p|li|ul|ol)\s*>/gi;

/** Whether a string might need sanitising or entity decoding. `&` counts as well as `<`, because
 * an entity carries no tag. Skipping the parser would leave `&amp;` where it belongs as `&`. */
const MARKUP_PATTERN = /[<&]/;

/** Built on first use because the zod schema imports this module, and a `DOMParser` built at
 * module scope would throw without a DOM. */
let htmlParser: DOMParser | undefined;

function readTextContent(html: string): string {
  htmlParser ??= new DOMParser();

  return htmlParser.parseFromString(html, "text/html").body.textContent ?? "";
}

/**
 * Strips a phrase body down to the tags the Phrase Text toolbar can produce, dropping every
 * attribute. Applied on write *and* on render, because clinician-authored HTML ends up inside a
 * note and so carries a stored-XSS risk at both ends.
 */
export function sanitizeDotPhraseBodyHtml(bodyHtml: string): string {
  return DOMPurify.sanitize(bodyHtml, {
    ALLOWED_TAGS: DOT_PHRASE_ALLOWED_BODY_TAGS,
    ALLOWED_ATTR: [],
  });
}

/** The sanitised body as a phrase stores and inserts it, without trailing empty paragraphs.
 * Interior empty paragraphs are blank lines the provider typed, so they stay. */
export function normalizeDotPhraseBodyHtml(bodyHtml: string): string {
  return removeTrailingEmptyParagraphs(sanitizeDotPhraseBodyHtml(bodyHtml));
}

/** Removes trailing empty paragraphs, from TipTap's trailing node after a list or an Enter at the
 * end, which would reach the note as a blank line. A paragraph is empty when it holds only
 * whitespace and `<br>`. Scans backwards once, so the cost stays linear in the body's length. */
function removeTrailingEmptyParagraphs(html: string): string {
  let end = html.length;

  for (;;) {
    let tail = end;
    while (tail > 0 && /\s/.test(html[tail - 1])) tail -= 1;

    if (tail < 4 || !html.startsWith("</p>", tail - 4)) break;

    const start = html.lastIndexOf("<p>", tail - 4);
    if (start === -1) break;

    const inner = html.slice(start + 3, tail - 4).replace(/<br>/g, "");
    if (/\S/.test(inner)) break;

    end = start;
  }

  return html.slice(0, end);
}

/** Collapses the whitespace a projection picks up from the markup. The newlines the block
 * boundaries just added are kept, so `description:contains` sees the prose the provider typed. */
function normalizePlainText(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/ ?\n ?/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * The body's plain-text projection, as stored after the separator in `description`.
 *
 * **Derived, never authored.** The attachment HTML is the source of truth, so this is an index
 * rebuilt from it on every write. Editing `description` directly, in a support fix or a migration,
 * leaves the record out of sync with nothing to catch it.
 */
export function deriveDotPhrasePlainText(bodyHtml: string): string {
  if (!bodyHtml) return "";

  if (!MARKUP_PATTERN.test(bodyHtml)) return normalizePlainText(bodyHtml);

  // Sanitising first stops a dropped tag from contributing a boundary, and keeps a `<script>` body
  // from reaching the projection as prose.
  const withBoundaries = sanitizeDotPhraseBodyHtml(bodyHtml).replace(BLOCK_BOUNDARY_PATTERN, "\n");

  // `textContent` decodes the entities: `&amp;` lands as `&`, or ampersand search breaks.
  return normalizePlainText(readTextContent(withBoundaries));
}

/**
 * Splits on the **first** occurrence only and keeps the remainder verbatim, so a body containing
 * the marker comes back whole. A description with no separator is malformed; the reader stays
 * forgiving and treats the whole string as a title.
 */
export function parseDotPhraseDescription(description: string): { title: string; body: string } {
  const separatorIndex = description.indexOf(DOT_PHRASE_TITLE_BODY_SEPARATOR);

  if (separatorIndex === -1) {
    return { title: description.trim(), body: "" };
  }

  return {
    title: description.slice(0, separatorIndex).trim(),
    body: description.slice(separatorIndex + DOT_PHRASE_TITLE_BODY_SEPARATOR.length),
  };
}

/** Save-time validation calls this on the title and on the body's **projection**, so a marker
 * split across tags (`--<strong>=</strong>--`) cannot slip past. */
export function containsDotPhraseSeparatorMarker(text: string): boolean {
  return text.includes(DOT_PHRASE_SEPARATOR_MARKER);
}

/**
 * Display only. Search has to strip the body separately, because this stops at the cap and would
 * miss anything later in a long phrase.
 *
 * The stored body is a plain-text projection already, so nothing is stripped here. A `<` or `&`
 * in it is prose a provider typed, such as `sats <95%` or `A & B`, and re-deriving would delete
 * it. A record holding raw HTML is a write-path bug to backfill.
 */
export function buildDotPhrasePreview(body: string): string {
  return body.replace(/\s+/g, " ").trim().slice(0, DOT_PHRASE_PREVIEW_MAX_LENGTH).trimEnd();
}

/**
 * Keeps a trigger on exactly one leading dot. The field starts as `"."`, so typing over it, pasting
 * `..labor` or deleting the dot still gives a well-formed trigger. Whitespace goes because a space
 * would end a trigger typed mid-note.
 *
 * Also how a stored trigger is rendered: `identifier.value` holds it without the leading dot, and
 * putting one back is the same operation.
 */
export function normalizeDotPhraseTrigger(raw: string): string {
  const body = raw.replace(/\s+/g, "").replace(/^\.+/, "");

  return `${DOT_PHRASE_TRIGGER_PREFIX}${body}`;
}

/**
 * The term a provider typed, as the server can match it. A stored trigger has no leading dot and
 * the table renders one, so searching `.nob` is searching for `nob`. Only the leading dots go:
 * `toDotPhraseTriggerIdentifierValue` would take the spaces out of a title search too.
 */
export function normalizeDotPhraseSearch(search: string | undefined): string {
  return search?.trim().replace(/^\.+/, "") ?? "";
}

/** The stored form of a trigger: `identifier.value` drops the leading dot, so `.nob` is `nob`. */
export function toDotPhraseTriggerIdentifierValue(trigger: string): string {
  return normalizeDotPhraseTrigger(trigger).slice(DOT_PHRASE_TRIGGER_PREFIX.length);
}

/** Joins a title to its body's projection. Always writes the separator, even for an empty body, so
 * every record the app produces has one. */
export function buildDotPhraseDescription(title: string, bodyHtml: string): string {
  return `${title.trim()}${DOT_PHRASE_TITLE_BODY_SEPARATOR}${deriveDotPhrasePlainText(bodyHtml)}`;
}

export interface DotPhraseBodyFields {
  description: string;
  content: DocumentReferenceContent[];
}

/**
 * The one function that writes either representation of a phrase body. `description` and `content`
 * come out together and go out in the same single-resource update, so they cannot disagree through
 * a partial write. Nothing else may assemble either field.
 */
export function buildDotPhraseBodyFields(title: string, bodyHtml: string): DotPhraseBodyFields {
  const normalizedHtml = normalizeDotPhraseBodyHtml(bodyHtml);

  return {
    description: buildDotPhraseDescription(title, normalizedHtml),
    content: [
      {
        attachment: {
          contentType: DOT_PHRASE_BODY_CONTENT_TYPE,
          // `base64Binary` has a 1-character minimum, so an empty body leaves `data` off; a `""`
          // would have the server reject the whole resource.
          ...(normalizedHtml ? { data: encodeBase64(normalizedHtml) } : {}),
        },
      },
    ],
  };
}

/** Whether a `content` entry is the phrase body. `contentType` may carry parameters, so
 * `text/html; charset=utf-8` counts the same as `text/html`. */
function isDotPhraseBodyContent(content: DocumentReferenceContent): boolean {
  return (
    content.attachment?.contentType?.split(";")[0]?.trim().toLowerCase() ===
    DOT_PHRASE_BODY_CONTENT_TYPE
  );
}

/**
 * The authoritative body, for editing a phrase in the drawer and for rendering it into a note.
 * Sanitised again on the way out, so a record written before an allowlist change — or by anything
 * other than this app — still cannot inject markup.
 *
 * Picks the body by `contentType`, not by position: a PDF prepended by another writer would reach
 * the editor as garbage the next save would persist.
 */
export function readDotPhraseBodyHtml(content: DocumentReferenceContent[] | undefined): string {
  const data = content?.find(isDotPhraseBodyContent)?.attachment?.data;

  if (!data) return "";

  return normalizeDotPhraseBodyHtml(decodeBase64(data));
}

export function readDotPhraseTrigger(documentReference: DocumentReference): string {
  return getIdentifier(documentReference, Systems.DotPhraseTriggerIdentifier) ?? "";
}

/**
 * Builds the row the table renders. `description` is passed through whole. Every reader splits it
 * with `parseDotPhraseDescription`, so the title/body boundary stays in one place. NEVER log any
 * of these values: a body is clinician-authored template text, so `DocumentReference.id` is the
 * only safe thing to report.
 */
export function toDotPhrase(documentReference: WithId<DocumentReference>): DotPhrase {
  return {
    id: documentReference.id,
    trigger: normalizeDotPhraseTrigger(readDotPhraseTrigger(documentReference)),
    description: documentReference.description ?? "",
    isArchived: documentReference.status === DOT_PHRASE_ARCHIVED_STATUS,
    // The write path fills `author[0].display`, so the table can name an editor without a second
    // fetch per row. An older record without it shows nothing rather than a bare reference.
    lastEditedBy: documentReference.author?.[0]?.display ?? "",
    lastEditedAt: documentReference.meta?.lastUpdated ?? documentReference.date ?? "",
    resource: documentReference,
  };
}

export function toDotPhraseFormValues(phrase: WithId<DocumentReference>): DotPhraseFormType {
  return {
    trigger: normalizeDotPhraseTrigger(readDotPhraseTrigger(phrase)),
    title: parseDotPhraseDescription(phrase.description ?? "").title,
    phraseText: readDotPhraseBodyHtml(phrase.content),
  };
}
